import api from '../../services/api';
import type { MsrReportData, MsrReportRawData } from './msrReportTypes';
import type {
  AzureCustomerCostResult,
  AzureLast3MonthsAnalysisResult,
  AzureSubscriptionCostResult,
} from '../../types/azureCost';
import type { KpiData, KpiLastUpdatedRow, KpiOperationalAgeingRow, KpiResilienceData } from './types';
import { getDurationPresetRange, type DurationPreset } from '../dashboard/utils/dateHelpers';
import {
  DEFAULT_PREFERENCES,
  dateTimeLocalInTimeZoneToIso,
  getTimezoneOffsetMinutesForZone,
} from '../../utils/dateFormatter';
import {
  appendResilienceCrossParams,
  type KpiResilienceCrossSlice,
} from './kpiResilienceCrossFilter';

/** GET /dashboard/filters — same contract as FILTERS_REFERENCE.md */
export interface KpiDashboardFiltersData {
  date: { years: number[]; months: string[]; days: number[] };
  ticketTypes: string[];
  priorities: string[];
  states: string[];
  customers: { id: string; accountName: string }[];
  assignmentGroups: { id: string; name: string }[];
}

const MONTH_AXIS_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** Fallback when API omits labels — trailing 12 months ending at `end` (local) */
function buildRollingMonthLabelsFromEnd(end: Date): string[] {
  const anchor = new Date(end.getFullYear(), end.getMonth(), 1);
  const out: string[] = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(anchor.getFullYear(), anchor.getMonth() - (11 - i), 1);
    const mon = MONTH_AXIS_ABBR[d.getMonth()] ?? 'Jan';
    const yy = String(d.getFullYear() % 100).padStart(2, '0');
    out.push(`${mon} ${yy}`);
  }
  return out;
}

export async function fetchKpiDashboardFilters(): Promise<KpiDashboardFiltersData> {
  const response = await api.get<{ success: boolean; data: KpiDashboardFiltersData }>('/dashboard/filters');
  if (!response.data?.success || !response.data.data) {
    throw new Error('Invalid dashboard filters response');
  }
  return response.data.data;
}

/** Backend GET /dashboard/charts response */
export interface KpiDashboardChartsApiData {
  summary: {
    ticketsTotal: number;
    incidentsVolume: number;
    requestsVolume: number;
    casesVolume: number;
    changesVolume: number;
    problemsVolume: number;
    mttrMinutes: string | null;
    incidentsMttrMinutes?: string | null;
    requestsMttrMinutes?: string | null;
    changeFailureRate: string;
    changesSuccessRate?: string;
    changesMttrMinutes?: string | null;
    ticketsAvgOpenDays: string | null;
    incidentsAvgOpenDays?: string | null;
    requestsAvgOpenDays?: string | null;
    casesAvgOpenDays?: string | null;
  };
  rates: {
    caseResponseSlaMetRate?: string;
    incidentOngoingSlaMetRate?: string;
    requestOngoingSlaMetRate?: string;
    incidentResolutionSlaMetRate?: string;
    requestResolutionSlaMetRate?: string;
    caseResponseSlaTicketCount?: number;
    incidentOngoingSlaTicketCount?: number;
    requestOngoingSlaTicketCount?: number;
    incidentResolutionSlaTicketCount?: number;
    requestResolutionSlaTicketCount?: number;
  };
  backlogAge: Record<string, number>;
  priorityCounts: Record<string, number>;
  slaBreachPct: Record<string, number>;
  slaBreachByType: { Case: number; Incident: number; Request: number };
  slaBreachBySlaMetric?: {
    caseResponse: number;
    incidentOngoing: number;
    incidentResolution: number;
    requestOngoing: number;
    requestResolution: number;
  };
  monthlyTrendCase: number[];
  monthlyTrendIncident: number[];
  monthlyTrendRequest: number[];
  monthlyTrendMonthLabels?: string[];
  monthlyTrendChange?: number[];
  monthlyTrendProblem?: number[];
  problemsWithKnowledgePct?: string;
  problemsVolumeByWeek?: Array<{ weekLabel: string; weekStart: string; count: number }>;
  problemsStateCustomerHeatmap?: {
    stateLabels: string[];
    stateKeys: string[];
    rows: Array<{ customerId: string; customerName: string; counts: number[] }>;
    maxCount: number;
  };
  problemsCustomerWeekHeatmap?: {
    weekLabels: string[];
    weekStarts: string[];
    rows: Array<{ customerId: string; customerName: string; counts: number[] }>;
    maxCount: number;
  };
  problemsByResolutionCode?: Array<{ key: string; label: string; count: number }>;
  problemsDetailPreview?: Array<{
    id: string;
    number: string;
    stateLabel: string;
    openedAt: string;
    assignmentGroup: string | null;
    company: string | null;
    knowledge: string;
    priorityLabel: string;
    problemStatement: string;
    resolutionLabel: string | null;
  }>;
  operationalAgeingByGroup?: KpiOperationalAgeingRow[];
  lastUpdatedByGroup?: KpiLastUpdatedRow[];
  incidentsByAssignmentGroup?: Array<{ groupName: string; count: number; slaBreachPct: number }>;
  requestsByCustomerTreemap?: Array<{ customerId: string; customerName: string; count: number }>;
  requestsByAssignmentGroup?: Array<{ groupName: string; count: number; slaBreachPct: number }>;
  casesVolumeByWeek?: Array<{ weekLabel: string; weekStart: string; count: number }>;
  casesByCustomerTreemap?: Array<{ customerId: string; customerName: string; count: number }>;
  casesByAssignmentGroup?: Array<{ groupName: string; count: number; slaBreachPct: number }>;
  changesByAssignmentGroup?: Array<{ groupName: string; count: number; successPct: number }>;
  changesCloseCodeSlices?: Array<{ key: string; label: string; count: number }>;
  changesByState?: Array<{ stateLabel: string; stateKey: string; count: number }>;
  changesByChangeType?: Array<{ typeLabel: string; typeKey: string; count: number }>;
  changeFailureRateByType?: Array<{ typeLabel: string; typeKey: string; failurePct: number }>;
  resilience?: KpiResilienceData;
  generatedAt: string;
}

const EMPTY_RESILIENCE: KpiResilienceData = {
  mttrHeatmap: {
    weekLabels: [],
    weekStarts: [],
    weekdayLabels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    mttrByWeekAndWeekday: [],
    countsByWeekAndWeekday: [],
  },
  mttrByPriority: [],
  mttrTrendByWeek: [],
  mttrByAssignmentGroup: [],
};

function normalizeResilience(raw: KpiResilienceData | undefined): KpiResilienceData {
  const r = raw ?? EMPTY_RESILIENCE;
  const hm = r.mttrHeatmap ?? EMPTY_RESILIENCE.mttrHeatmap;
  return {
    ...r,
    mttrHeatmap: {
      ...EMPTY_RESILIENCE.mttrHeatmap,
      ...hm,
      weekStarts: hm.weekStarts ?? [],
    },
  };
}

export interface KpiChartsQueryParams {
  dateFrom?: string;
  dateTo?: string;
  timezoneOffset: string;
  /** IANA zone used for Duration wall-clock (matches table date formatting). */
  timezone?: string;
  timeRange?: string;
  durationDays?: string;
  ticketTypes?: string;
  states?: string;
  priority?: string;
  customerId?: string;
  assignmentGroupId?: string;
  assignedToMe?: string;
  assignedToId?: string;
  /** Resilience tab cross-filter (optional). */
  resilienceHeatmapWeek?: string;
  resilienceHeatmapDow?: string;
  resiliencePriority?: string;
  resilienceGroupName?: string;
}

/** Browser offset minutes, or IANA zone matching KPI date display (default Asia/Kolkata). */
export type KpiTimeContext = number | { offsetMinutes: number; timeZone?: string };

function resolveKpiTimeContext(tz: KpiTimeContext): { offsetMinutes: number; timeZone: string } {
  if (typeof tz === 'number') {
    return { offsetMinutes: tz, timeZone: DEFAULT_PREFERENCES.timezone };
  }
  return {
    offsetMinutes: tz.offsetMinutes,
    timeZone: tz.timeZone || DEFAULT_PREFERENCES.timezone,
  };
}

/** KPI duration uses the same preset/custom strings as Service Request / FilterDropdown time-range. */
export function buildKpiChartsQueryParams(
  filterValues: Record<string, string | string[]>,
  timezoneOffset: KpiTimeContext
): KpiChartsQueryParams {
  const { timeZone } = resolveKpiTimeContext(timezoneOffset);
  const zoneOffset = getTimezoneOffsetMinutesForZone(timeZone);
  const timeRange = (filterValues.duration as string) || '';
  let dateFrom = '';
  let dateTo = '';
  let timeRangeParam = '';
  let durationDaysFallback = '';

  if (timeRange.startsWith('custom:')) {
    const [, dates] = timeRange.split('custom:');
    const [from, to] = (dates || '').split(',');
    dateFrom = from || '';
    dateTo = to || '';
  } else if (timeRange.startsWith('preset:')) {
    const presetName = timeRange.replace('preset:', '') as DurationPreset;
    const range = getDurationPresetRange(presetName);
    dateFrom = range.from;
    dateTo = range.to;
  } else if (/^(30|60|90)$/.test(timeRange)) {
    durationDaysFallback = timeRange;
  } else if (timeRange) {
    timeRangeParam = timeRange;
  }

  if (dateFrom) {
    dateFrom = dateTimeLocalInTimeZoneToIso(dateFrom, timeZone, false) || dateFrom;
  }
  if (dateTo) {
    dateTo = dateTimeLocalInTimeZoneToIso(dateTo, timeZone, true) || dateTo;
  }

  const ticketType = filterValues.ticketType;
  const state = filterValues.state;
  const priority = (filterValues.priority as string) || 'All';
  const customer = filterValues.customer as string;
  const groups = filterValues.assignmentGroup;
  const assignedToMe = (filterValues.assignedToMe as string) || '';
  const assignedTo = (filterValues.assignedTo as string) || '';

  const params: KpiChartsQueryParams = {
    timezoneOffset: String(zoneOffset),
    timezone: timeZone,
    priority,
  };

  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (timeRangeParam) params.timeRange = timeRangeParam;
  if (durationDaysFallback) params.durationDays = durationDaysFallback;

  if (Array.isArray(ticketType) && ticketType.length > 0) {
    params.ticketTypes = ticketType.join(',');
  }
  if (Array.isArray(state) && state.length > 0) {
    params.states = state.join(',');
  }
  if (customer && customer !== 'All') {
    params.customerId = customer;
  }
  if (Array.isArray(groups) && groups.length > 0) {
    params.assignmentGroupId = groups.join(',');
  }
  if (assignedToMe) params.assignedToMe = assignedToMe;
  if (assignedTo) params.assignedToId = assignedTo;

  return params;
}

/** Same as charts but omits assignee params so GET /dashboard/assignable-users matches non-assignee scope. */
export function buildKpiAssignableUsersQueryParams(
  filterValues: Record<string, string | string[]>,
  timezoneOffset: KpiTimeContext
): KpiChartsQueryParams {
  const p = { ...buildKpiChartsQueryParams(filterValues, timezoneOffset) };
  delete p.assignedToMe;
  delete p.assignedToId;
  return p;
}

export async function fetchKpiAssignableUsers(
  filterValues: Record<string, string | string[]>,
  timezoneOffset: KpiTimeContext
): Promise<Array<{ id: string; firstName?: string; lastName?: string; email?: string }>> {
  const params = buildKpiAssignableUsersQueryParams(filterValues, timezoneOffset);
  const response = await api.get<{
    success: boolean;
    data?: { users: Array<{ id: string; firstName?: string; lastName?: string; email?: string }> };
  }>('/dashboard/assignable-users', { params });
  if (!response.data?.success || !response.data.data?.users) {
    throw new Error('Invalid assignable users response');
  }
  return response.data.data.users;
}

export function mapApiToKpiData(api: KpiDashboardChartsApiData): KpiData {
  const { summary, rates } = api;
  const trendLen = api.monthlyTrendCase.length;
  const fallbackTrendLen = trendLen > 0 ? trendLen : 12;
  return {
    ticketsTotal: summary.ticketsTotal,
    incidentsVolume: summary.incidentsVolume,
    requestsVolume: summary.requestsVolume,
    casesVolume: summary.casesVolume,
    problemsVolume: summary.problemsVolume ?? 0,
    changesVolume: summary.changesVolume ?? 0,
    mttrMinutes: summary.mttrMinutes ?? '—',
    incidentsMttrMinutes: summary.incidentsMttrMinutes ?? summary.mttrMinutes ?? '—',
    requestsMttrMinutes: summary.requestsMttrMinutes ?? '—',
    changeFailureRate: summary.changeFailureRate,
    changesSuccessRate: summary.changesSuccessRate ?? '0.0',
    changesMttrMinutes: summary.changesMttrMinutes ?? '—',
    ticketsAvgOpenDays: summary.ticketsAvgOpenDays ?? '—',
    incidentsAvgOpenDays: summary.incidentsAvgOpenDays ?? summary.ticketsAvgOpenDays ?? '—',
    requestsAvgOpenDays: summary.requestsAvgOpenDays ?? summary.ticketsAvgOpenDays ?? '—',
    casesAvgOpenDays: summary.casesAvgOpenDays ?? summary.ticketsAvgOpenDays ?? '—',
    caseResponseSlaMetRate: rates.caseResponseSlaMetRate ?? '0.00',
    incidentOngoingSlaMetRate: rates.incidentOngoingSlaMetRate ?? '0.00',
    requestOngoingSlaMetRate: rates.requestOngoingSlaMetRate ?? '0.00',
    incidentResolutionSlaMetRate: rates.incidentResolutionSlaMetRate ?? '0.00',
    requestResolutionSlaMetRate: rates.requestResolutionSlaMetRate ?? '0.00',
    caseResponseSlaTicketCount: rates.caseResponseSlaTicketCount ?? 0,
    incidentOngoingSlaTicketCount: rates.incidentOngoingSlaTicketCount ?? 0,
    requestOngoingSlaTicketCount: rates.requestOngoingSlaTicketCount ?? 0,
    incidentResolutionSlaTicketCount: rates.incidentResolutionSlaTicketCount ?? 0,
    requestResolutionSlaTicketCount: rates.requestResolutionSlaTicketCount ?? 0,
    backlogAge: api.backlogAge,
    priorityCounts: api.priorityCounts,
    slaBreachPct: api.slaBreachPct,
    slaBreachByType: {
      Case: api.slaBreachByType.Case,
      Incident: api.slaBreachByType.Incident,
      Request: api.slaBreachByType.Request,
    },
    slaBreachBySlaMetric: api.slaBreachBySlaMetric ?? {
      caseResponse: 0,
      incidentOngoing: 0,
      incidentResolution: 0,
      requestOngoing: 0,
      requestResolution: 0,
    },
    monthlyTrend: api.monthlyTrendCase,
    monthlyTrendCase: api.monthlyTrendCase,
    monthlyTrendIncident: api.monthlyTrendIncident,
    monthlyTrendRequest: api.monthlyTrendRequest,
    monthlyTrendMonthLabels:
      api.monthlyTrendMonthLabels?.length === trendLen && trendLen > 0
        ? api.monthlyTrendMonthLabels
        : buildRollingMonthLabelsFromEnd(new Date()),
    monthlyTrendChange: api.monthlyTrendChange ?? Array(fallbackTrendLen).fill(0),
    monthlyTrendProblem: api.monthlyTrendProblem ?? Array(fallbackTrendLen).fill(0),
    problemsWithKnowledgePct: api.problemsWithKnowledgePct ?? '0.0',
    problemsVolumeByWeek: api.problemsVolumeByWeek ?? [],
    problemsStateCustomerHeatmap: api.problemsStateCustomerHeatmap ?? {
      stateLabels: [],
      stateKeys: [],
      rows: [],
      maxCount: 1,
    },
    problemsCustomerWeekHeatmap: api.problemsCustomerWeekHeatmap ?? {
      weekLabels: [],
      weekStarts: [],
      rows: [],
      maxCount: 1,
    },
    problemsByResolutionCode: api.problemsByResolutionCode ?? [],
    problemsDetailPreview: api.problemsDetailPreview ?? [],
    operationalAgeingByGroup: api.operationalAgeingByGroup ?? [],
    lastUpdatedByGroup: api.lastUpdatedByGroup ?? [],
    incidentsByAssignmentGroup: api.incidentsByAssignmentGroup ?? [],
    requestsByCustomerTreemap: api.requestsByCustomerTreemap ?? [],
    requestsByAssignmentGroup: api.requestsByAssignmentGroup ?? [],
    casesVolumeByWeek: api.casesVolumeByWeek ?? [],
    casesByCustomerTreemap: api.casesByCustomerTreemap ?? [],
    casesByAssignmentGroup: api.casesByAssignmentGroup ?? [],
    changesByAssignmentGroup: api.changesByAssignmentGroup ?? [],
    changesCloseCodeSlices: api.changesCloseCodeSlices ?? [],
    changesByState: api.changesByState ?? [],
    changesByChangeType: api.changesByChangeType ?? [],
    changeFailureRateByType: api.changeFailureRateByType ?? [],
    resilience: normalizeResilience(api.resilience),
  };
}

export async function fetchKpiDashboardCharts(
  filterValues: Record<string, string | string[]>,
  timezoneOffset: KpiTimeContext,
  resilienceCross?: KpiResilienceCrossSlice | null
): Promise<KpiData> {
  const params = buildKpiChartsQueryParams(filterValues, timezoneOffset);
  appendResilienceCrossParams(params, resilienceCross ?? null);
  const response = await api.get<{ success: boolean; data: KpiDashboardChartsApiData }>(
    '/dashboard/charts',
    { params }
  );
  if (!response.data?.success || !response.data.data) {
    throw new Error('Invalid dashboard charts response');
  }
  return mapApiToKpiData(response.data.data);
}

/** GET /dashboard/open-backlog-detail — ticket rows for Excel export */
export interface OpenBacklogDetailRow {
  number: string;
  shortDescription: string;
  type: string;
  priority: string;
  state: string;
  assignmentGroup: string;
  assignedTo: string | null;
  company: string | null;
  lastUpdatedTime: string;
  lastUpdatedBy: string | null;
  ticketCreated: string;
  lastUpdatedDuration: string;
  lastUpdatedAgeing: string;
  /** Whole days from ticket created to export (Excel-style date diff). */
  durationDays: number;
  ageingBucket: string;
  remarks: string;
}

export type OpenBacklogDetailExportKind = 'ageing' | 'lastUpdated';

export async function fetchOpenBacklogOperationalDetail(
  filterValues: Record<string, string | string[]>,
  timezoneOffset: KpiTimeContext,
  exportKind: OpenBacklogDetailExportKind
): Promise<{ rows: OpenBacklogDetailRow[]; truncated: boolean; totalMatched: number }> {
  const params = buildKpiChartsQueryParams(filterValues, timezoneOffset);
  const response = await api.get<{
    success: boolean;
    data?: {
      rows: OpenBacklogDetailRow[];
      truncated: boolean;
      totalMatched: number;
      exportKind: string;
    };
    message?: string;
  }>('/dashboard/open-backlog-detail', {
    params: { ...params, exportKind },
  });
  if (!response.data?.success || !response.data.data) {
    throw new Error(response.data?.message || 'Invalid open backlog detail response');
  }
  const { rows, truncated, totalMatched } = response.data.data;
  return { rows, truncated, totalMatched };
}

/** GET /dashboard/kpi-heatmap-customer — Heatmap (Customer) tab */
export interface KpiHeatmapCustomerApiData {
  heatmap: {
    weekLabels: string[];
    weekStarts: string[];
    rows: { customerId: string; customerName: string; counts: number[] }[];
    maxCount: number;
  };
  volumeByWeek: { weekLabel: string; weekStart: string; incident: number; request: number }[];
  backlogPriority: {
    Critical: number;
    High: number;
    Moderate: number;
    Low: number;
    Planning: number;
  };
  generatedAt: string;
}

export async function fetchKpiHeatmapCustomerView(
  filterValues: Record<string, string | string[]>,
  timezoneOffset: KpiTimeContext
): Promise<KpiHeatmapCustomerApiData> {
  const params = buildKpiChartsQueryParams(filterValues, timezoneOffset);
  const response = await api.get<{ success: boolean; data: KpiHeatmapCustomerApiData }>(
    '/dashboard/kpi-heatmap-customer',
    { params }
  );
  if (!response.data?.success || !response.data.data) {
    throw new Error('Invalid heatmap customer response');
  }
  return response.data.data;
}

/** GET /dashboard/kpi-heatmap-ci — Heatmap (CI) tab */
export interface KpiHeatmapCiApiData {
  heatmap: {
    weekLabels: string[];
    weekStarts: string[];
    maxCount: number;
    rows: Array<{
      ciResourceId: string | null;
      ciLabel: string;
      counts: number[];
    }>;
  };
  backlogByAssignmentGroup: Array<{ groupName: string; count: number; slaBreachPct: number }>;
  assigneeOpenBacklog: Array<{
    userId: string;
    displayName: string;
    count: number;
    avgOpenDays: number;
  }>;
  ciTreemap: Array<{ ciResourceId: string | null; ciLabel: string; count: number }>;
  generatedAt: string;
}

export interface KpiHeatmapCiFocus {
  resourceId: string | null;
  label: string | null;
  weekStart: string | null;
  assignmentGroupName: string | null;
  assigneeUserId: string | null;
}

export async function fetchKpiHeatmapCiView(
  filterValues: Record<string, string | string[]>,
  timezoneOffset: KpiTimeContext,
  focus: KpiHeatmapCiFocus
): Promise<KpiHeatmapCiApiData> {
  const params: Record<string, string> = {
    ...buildKpiChartsQueryParams(filterValues, timezoneOffset),
  } as Record<string, string>;
  if (focus.resourceId) params.focusCiResourceId = focus.resourceId;
  if (focus.label) params.focusCiLabel = focus.label;
  if (focus.weekStart) params.focusCiWeekStart = focus.weekStart;
  if (focus.assignmentGroupName) params.focusCiAssignmentGroup = focus.assignmentGroupName;
  if (focus.assigneeUserId) params.focusCiAssigneeId = focus.assigneeUserId;
  const response = await api.get<{ success: boolean; data: KpiHeatmapCiApiData }>(
    '/dashboard/kpi-heatmap-ci',
    { params }
  );
  if (!response.data?.success || !response.data.data) {
    throw new Error('Invalid heatmap CI response');
  }
  return response.data.data;
}

/** KPI-only drill-down (separate from legacy dashboard ticket tables). */
export type KpiDrilldownSpec =
  | { kind: 'metric'; key: string }
  | { kind: 'rate'; key: string }
  | { kind: 'backlog'; bucket: string }
  | { kind: 'priority'; priority: string }
  | { kind: 'sla_breach'; breachType: 'Case' | 'Incident' | 'Request' }
  | { kind: 'trend'; monthIndex: number; series: 'Case' | 'Incident' | 'Request' | 'Change' | 'Problem' }
  | { kind: 'heatmap_cell'; customerId: string; weekStart: string }
  | { kind: 'heatmap_priority'; priority: string }
  | { kind: 'heatmap_volume_week'; weekStart: string; series: 'INCIDENT' | 'SERVICE_REQUEST' | 'CASE' | 'PROBLEM' }
  | { kind: 'problem_state_customer'; customerId: string; stateKey: string }
  | { kind: 'problem_week_customer'; customerId: string; weekStart: string }
  | { kind: 'problem_resolution_code'; resolutionKey: string }
  | { kind: 'assignment_group'; groupName: string; ticketType?: 'INCIDENT' | 'SERVICE_REQUEST' | 'CASE' | 'CHANGE' }
  | { kind: 'kpi_volume_customer'; customerId: string; ticketType: 'INCIDENT' | 'SERVICE_REQUEST' | 'CASE' }
  | {
      kind: 'change_chart';
      chartKind: 'close_code' | 'state' | 'change_type' | 'failure_by_type';
      value: string;
    }
  | {
      kind: 'heatmap_ci_cell';
      ciResourceId: string | null;
      ciLabel: string;
      weekStart: string;
    }
  | { kind: 'heatmap_ci_volume'; ciResourceId: string | null; ciLabel: string }
  | {
      kind: 'ci_backlog_assignment_group';
      groupName: string;
      ciResourceId?: string | null;
      ciLabel?: string;
      weekStart?: string | null;
    }
  | {
      kind: 'ci_backlog_assignee';
      userId: string;
      displayName?: string;
      ciResourceId?: string | null;
      ciLabel?: string;
      weekStart?: string | null;
    }
  | { kind: 'resilience_heatmap_cell'; weekStart: string; isoDow: number }
  | { kind: 'resilience_priority'; priorityLabel: string }
  | { kind: 'resilience_trend_week'; weekStart: string; series: 'INCIDENT' | 'SERVICE_REQUEST' }
  | { kind: 'resilience_assignment_group'; groupName: string }
  | {
      kind: 'sla_view_bucket';
      ticketType: 'CASE' | 'INCIDENT' | 'SERVICE_REQUEST';
      bucket: SlaViewBucketKey;
      /** ANY = Case overall; RESOLUTION / ONGOING for Incident & SR charts. */
      targetScope?: SlaViewTargetScope;
    };

export type SlaViewBucketKey = '0-24' | '25-49' | '50-74' | '75-99' | 'breached';

export type SlaViewTargetScope = 'ANY' | 'RESOLUTION' | 'ONGOING';

export interface SlaViewBucketCountDto {
  key: SlaViewBucketKey;
  label: string;
  count: number;
}

export interface SlaViewTypeDistributionDto {
  ticketType: 'CASE' | 'INCIDENT' | 'SERVICE_REQUEST';
  targetScope?: SlaViewTargetScope;
  label: string;
  total: number;
  buckets: SlaViewBucketCountDto[];
}

export interface KpiSlaViewDistributionDto {
  case: SlaViewTypeDistributionDto;
  incidentResolution: SlaViewTypeDistributionDto;
  incidentOngoing: SlaViewTypeDistributionDto;
  requestResolution: SlaViewTypeDistributionDto;
  requestOngoing: SlaViewTypeDistributionDto;
}

/** Matches UnifiedPulseReportTable SLA columns (Start, Target, Status, Breached At, Reason). */
export interface KpiDrilldownSlaCellDto {
  startTime: string | null;
  targetTime: string | null;
  status: string;
  breachedAt: string | null;
  breachReason: string | null;
  /** Completed % from SlaInstance engine (null when SLA missing / invalid duration). */
  completedPct?: number | null;
  /** Remaining % = 100 - completedPct when completedPct is known. */
  remainingPct?: number | null;
}

export type KpiDrilldownSortBy = 'createdAt' | 'team' | 'customer';

export interface KpiDrilldownTicketDto {
  id: string;
  number: string;
  shortDescription: string;
  ticketType: string;
  taskTypeLabel: string;
  typeDisplay: string;
  /** Raw Prisma enum for Pulse-style priority badges */
  priority: string;
  /** Raw Prisma enum for Pulse-style state badges */
  state: string;
  priorityLabel: string;
  stateLabel: string;
  assignmentGroup: string | null;
  /** Team = assignment group (MSR / bandwidth synonym). */
  team?: string | null;
  assignedTo: string | null;
  company: string | null;
  customerContact: string | null;
  responseSla: KpiDrilldownSlaCellDto;
  ongoingSla: KpiDrilldownSlaCellDto;
  resolutionSla: KpiDrilldownSlaCellDto;
  createdAt: string;
  /** Post-SLA fields (UnifiedPulseReportTable) */
  createdBy: string | null;
  ticketLastUpdatedTime: string;
  lastUpdatedBy: string | null;
  channel: string | null;
  resource: string | null;
  ticketCreated: string;
  ticketResolved: string | null;
  ticketResolvedBy: string | null;
  ticketClosed: string | null;
  ticketClosedBy: string | null;
  onHoldReason: string | null;
  ticketClosureRemarks: string | null;
  ticketDurationMins: number | null;
  changeStatus: string | null;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  actualStartDate: string | null;
  actualEndDate: string | null;
  closureCode: string | null;
  rcaPresentedBy: string | null;
  problemDate: string | null;
}

export interface KpiDrilldownResponse {
  filterLabel: string;
  tickets: KpiDrilldownTicketDto[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

function drillToQueryParams(spec: KpiDrilldownSpec): Record<string, string> {
  switch (spec.kind) {
    case 'metric':
      return { drillKind: 'metric', drillKey: spec.key };
    case 'rate':
      return { drillKind: 'rate', drillKey: spec.key };
    case 'backlog':
      return { drillKind: 'backlog', drillBucket: spec.bucket };
    case 'priority':
      return { drillKind: 'priority', drillPriority: spec.priority };
    case 'sla_breach':
      return { drillKind: 'sla_breach', drillBreachType: spec.breachType };
    case 'trend':
      return {
        drillKind: 'trend',
        drillMonthIndex: String(spec.monthIndex),
        drillTrendSeries: spec.series,
      };
    case 'heatmap_cell':
      return {
        drillKind: 'heatmap_cell',
        drillHeatmapCustomerId: spec.customerId,
        drillHeatmapWeekStart: spec.weekStart,
      };
    case 'heatmap_priority':
      return { drillKind: 'heatmap_priority', drillPriority: spec.priority };
    case 'heatmap_volume_week':
      return {
        drillKind: 'heatmap_volume_week',
        drillHeatmapWeekStart: spec.weekStart,
        drillVolumeSeries: spec.series,
      };
    case 'assignment_group':
      return {
        drillKind: 'assignment_group',
        drillAssignmentGroupName: spec.groupName,
        ...(spec.ticketType != null ? { drillAssignmentGroupTicketType: spec.ticketType } : {}),
      };
    case 'kpi_volume_customer':
      return {
        drillKind: 'kpi_volume_customer',
        drillKpiVolumeCustomerId: spec.customerId,
        drillKpiVolumeTicketType: spec.ticketType,
      };
    case 'change_chart':
      return {
        drillKind: 'change_chart',
        drillChangeChartKind: spec.chartKind,
        drillChangeChartValue: spec.value,
      };
    case 'problem_state_customer':
      return {
        drillKind: 'problem_state_customer',
        drillProblemCustomerId: spec.customerId,
        drillProblemStateKey: spec.stateKey,
      };
    case 'problem_week_customer':
      return {
        drillKind: 'problem_week_customer',
        drillProblemCustomerId: spec.customerId,
        drillProblemWeekStart: spec.weekStart,
      };
    case 'problem_resolution_code':
      return {
        drillKind: 'problem_resolution_code',
        drillProblemResolutionCode: spec.resolutionKey,
      };
    case 'heatmap_ci_cell':
      return {
        drillKind: 'heatmap_ci_cell',
        drillHeatmapCiWeekStart: spec.weekStart,
        ...(spec.ciResourceId ? { drillHeatmapCiResourceId: spec.ciResourceId } : {}),
        drillHeatmapCiLabel: spec.ciLabel,
      };
    case 'heatmap_ci_volume':
      return {
        drillKind: 'heatmap_ci_volume',
        ...(spec.ciResourceId ? { drillHeatmapCiResourceId: spec.ciResourceId } : {}),
        drillHeatmapCiLabel: spec.ciLabel,
      };
    case 'ci_backlog_assignment_group':
      return {
        drillKind: 'ci_backlog_assignment_group',
        drillAssignmentGroupName: spec.groupName,
        ...(spec.ciResourceId ? { drillHeatmapCiResourceId: spec.ciResourceId } : {}),
        ...(spec.ciLabel ? { drillHeatmapCiLabel: spec.ciLabel } : {}),
        ...(spec.weekStart ? { drillHeatmapCiWeekStart: spec.weekStart } : {}),
      };
    case 'ci_backlog_assignee':
      return {
        drillKind: 'ci_backlog_assignee',
        drillAssigneeUserId: spec.userId,
        ...(spec.ciResourceId ? { drillHeatmapCiResourceId: spec.ciResourceId } : {}),
        ...(spec.ciLabel ? { drillHeatmapCiLabel: spec.ciLabel } : {}),
        ...(spec.weekStart ? { drillHeatmapCiWeekStart: spec.weekStart } : {}),
      };
    case 'resilience_heatmap_cell':
      return {
        drillKind: 'resilience_heatmap_cell',
        drillResilienceWeekStart: spec.weekStart,
        drillResilienceIsoDow: String(spec.isoDow),
      };
    case 'resilience_priority':
      return {
        drillKind: 'resilience_priority',
        drillResiliencePriority: spec.priorityLabel,
      };
    case 'resilience_trend_week':
      return {
        drillKind: 'resilience_trend_week',
        drillResilienceWeekStart: spec.weekStart,
        drillVolumeSeries: spec.series,
      };
    case 'resilience_assignment_group':
      return {
        drillKind: 'resilience_assignment_group',
        drillAssignmentGroupName: spec.groupName,
      };
    case 'sla_view_bucket':
      return {
        drillKind: 'sla_view_bucket',
        drillSlaViewTicketType: spec.ticketType,
        drillSlaViewBucket: spec.bucket,
        ...(spec.targetScope && spec.targetScope !== 'ANY'
          ? { drillSlaViewTargetScope: spec.targetScope }
          : {}),
      };
    default: {
      const _x: never = spec;
      throw new Error(`Unhandled drill kind: ${JSON.stringify(_x)}`);
    }
  }
}

export async function fetchKpiSlaViewDistribution(
  filterValues: Record<string, string | string[]>,
  timezoneOffset: KpiTimeContext
): Promise<KpiSlaViewDistributionDto> {
  const base = buildKpiChartsQueryParams(filterValues, timezoneOffset);
  const response = await api.get<{ success: boolean; data: KpiSlaViewDistributionDto }>(
    '/dashboard/kpi-sla-view',
    { params: base }
  );
  if (!response.data?.success || !response.data.data) {
    throw new Error('Invalid KPI SLA View response');
  }
  return response.data.data;
}

export async function fetchKpiDrilldownTickets(
  filterValues: Record<string, string | string[]>,
  timezoneOffset: KpiTimeContext,
  spec: KpiDrilldownSpec,
  page: number,
  limit: number,
  resilienceCross?: KpiResilienceCrossSlice | null,
  sortBy?: KpiDrilldownSortBy | null
): Promise<KpiDrilldownResponse> {
  const base = buildKpiChartsQueryParams(filterValues, timezoneOffset);
  appendResilienceCrossParams(base, resilienceCross ?? null);
  const response = await api.get<{ success: boolean; data: KpiDrilldownResponse }>(
    '/dashboard/kpi-drilldown',
    {
      params: {
        ...base,
        ...drillToQueryParams(spec),
        page: String(page),
        limit: String(limit),
        ...(sortBy && sortBy !== 'createdAt' ? { sortBy } : {}),
      },
    }
  );
  if (!response.data?.success || !response.data.data) {
    throw new Error('Invalid KPI drill-down response');
  }
  return response.data.data;
}

/** Matches server `parseKpiDrilldownQuery` cap — use when paging through every drill-down row for export. */
export const KPI_DRILLDOWN_MAX_PAGE_LIMIT = 200;

/**
 * Fetches every ticket row for a KPI drill-down scope (same filters as the table), paging until all
 * server pages are consumed. Intended for CSV/Excel exports; UI tables should keep using paged fetches.
 */
export async function fetchAllKpiDrilldownTickets(
  filterValues: Record<string, string | string[]>,
  timezoneOffset: KpiTimeContext,
  spec: KpiDrilldownSpec,
  resilienceCross?: KpiResilienceCrossSlice | null
): Promise<KpiDrilldownTicketDto[]> {
  const limit = KPI_DRILLDOWN_MAX_PAGE_LIMIT;
  const out: KpiDrilldownTicketDto[] = [];
  let page = 1;
  for (;;) {
    const res = await fetchKpiDrilldownTickets(
      filterValues,
      timezoneOffset,
      spec,
      page,
      limit,
      resilienceCross ?? null
    );
    out.push(...res.tickets);
    if (res.tickets.length === 0 || page >= res.totalPages) break;
    page += 1;
  }
  return out;
}

export async function fetchMsrReport(
  filterValues: Record<string, string | string[]>,
  timezoneOffset: KpiTimeContext
): Promise<MsrReportData> {
  const customer = filterValues.customer as string;
  if (!customer || customer === 'All') {
    throw new Error('Select a specific customer to generate the MSR report.');
  }

  const params = buildKpiChartsQueryParams(filterValues, timezoneOffset);
  const response = await api.get<{ success: boolean; data: MsrReportData; message?: string }>(
    '/dashboard/msr-report',
    { params }
  );
  if (!response.data?.success || !response.data.data) {
    throw new Error(response.data?.message ?? 'Invalid MSR report response');
  }
  return response.data.data;
}

export async function fetchMsrReportRawData(
  filterValues: Record<string, string | string[]>,
  timezoneOffset: KpiTimeContext
): Promise<MsrReportRawData> {
  const customer = filterValues.customer as string;
  if (!customer || customer === 'All') {
    throw new Error('Select a specific customer to export MSR raw data.');
  }

  const params = buildKpiChartsQueryParams(filterValues, timezoneOffset);
  const response = await api.get<{ success: boolean; data: MsrReportRawData; message?: string }>(
    '/dashboard/msr-report/raw-data',
    { params }
  );
  if (!response.data?.success || !response.data.data) {
    throw new Error(response.data?.message ?? 'Invalid MSR raw data response');
  }
  return response.data.data;
}

async function fetchAzureCostSection<T>(
  path: string,
  customerId: string,
  month: number,
  year: number,
  options?: { timeoutMs?: number }
): Promise<T> {
  if (!customerId || customerId === 'All') {
    throw new Error('Select a specific customer to load Azure cost data.');
  }

  const response = await api.get<{
    success: boolean;
    data: T;
    message?: string;
  }>(path, {
    params: { customerId, month, year },
    timeout: options?.timeoutMs,
  });

  if (!response.data?.success || !response.data.data) {
    throw new Error(response.data?.message ?? 'Failed to load Azure cost data');
  }

  return response.data.data;
}

export async function fetchAzureSubscriptionCost(
  customerId: string,
  month: number,
  year: number
): Promise<AzureSubscriptionCostResult> {
  return fetchAzureCostSection<AzureSubscriptionCostResult>(
    '/dashboard/azure-cost/subscription',
    customerId,
    month,
    year
  );
}

export async function fetchAzureLast3MonthsAnalysis(
  customerId: string,
  month: number,
  year: number
): Promise<AzureLast3MonthsAnalysisResult> {
  return fetchAzureCostSection<AzureLast3MonthsAnalysisResult>(
    '/dashboard/azure-cost/last-3-months-analysis',
    customerId,
    month,
    year,
    { timeoutMs: 600_000 }
  );
}

/** @deprecated Prefer section-specific fetch functions for faster MSR loads. */
export async function fetchAzureCost(
  customerId: string,
  month: number,
  year: number
): Promise<AzureCustomerCostResult> {
  if (!customerId || customerId === 'All') {
    throw new Error('Select a specific customer to load Azure cost data.');
  }

  const response = await api.get<{
    success: boolean;
    data: AzureCustomerCostResult;
    message?: string;
  }>('/dashboard/azure-cost', {
    params: { customerId, month, year },
  });

  if (!response.data?.success || !response.data.data) {
    throw new Error(response.data?.message ?? 'Failed to load Azure cost data');
  }

  return response.data.data;
}
